import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, ExternalLink, Globe, Instagram, MapPin, Youtube, Facebook, Share2 } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { NONPROFIT_ENTRIES } from './NonProfitOrg';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop';

// Same key function NonProfitOrg.tsx uses for its managed collection, so the
// slug in the URL always matches the entry the grid card linked from.
export function nonProfitSlug(e: any, i: number) {
  return slugify(e.name || String(i));
}

// Collect every contact/social URL a listing has, deduped, with a label and
// icon — mirrors the button logic on the grid cards.
function collectContacts(e: any) {
  const candidates: { url?: string; label?: string }[] = [
    { url: e.website, label: 'Website' },
    { url: e.socialMediaUrl },
    { url: e.instagram, label: 'Instagram' },
    { url: e.facebook, label: 'Facebook' },
    { url: e.youtube, label: 'YouTube' },
    { url: e.tiktok, label: 'TikTok' },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => {
      const u = c.url.toLowerCase();
      if (u.includes('facebook.com')) return { ...c, Icon: Facebook, label: 'Facebook' };
      if (u.includes('youtube.com') || u.includes('youtu.be')) return { ...c, Icon: Youtube, label: 'YouTube' };
      if (u.includes('instagram.com')) return { ...c, Icon: Instagram, label: 'Instagram' };
      if (u.includes('tiktok.com')) return { ...c, Icon: Share2, label: 'TikTok' };
      return { ...c, Icon: Globe, label: c.label || 'Website' };
    });
}

export default function NonProfitDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('non_profit_org', NONPROFIT_ENTRIES, (e, i) => nonProfitSlug(e, i));

  const entryIndex = mc.items.findIndex((e: any, i: number) => nonProfitSlug(e, i) === slug);
  const entry = entryIndex >= 0 ? mc.items[entryIndex] : undefined;

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Organization Not Found | Dallas Desi Scoop"
          description="This non-profit organization listing could not be found."
          canonical="/non-profit-org"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Organization listing not found.</p>
        <Link to="/non-profit-org" className="text-[#082f6d] font-semibold hover:underline">
          ← Back to Non-Profit Organizations
        </Link>
      </div>
    );
  }

  const name = entry.name || 'Organization';
  const image = entry.image || FALLBACK_IMAGE;
  const address = entry.fullAddress || entry.address || '';
  const contacts = collectContacts(entry);
  const hasAnySocial = countSocialLinks(entry) > 0 || !!entry.socialMediaUrl;
  const canonicalPath = `/non-profit-org/${slug}`;
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}`)}`;

  const title = `${name} | South Asian Non-Profit Organization in Dallas–Fort Worth | Dallas Desi Scoop`;
  const metaDescription = `${name}${address ? ` in ${address}` : ''} is a South Asian non-profit organization serving the Dallas–Fort Worth desi community. Find description, programs, contact details, and ways to get involved on Dallas Desi Scoop.`;

  const jsonLd = [
    {
      '@type': 'NGO',
      '@id': `${BASE_URL}${canonicalPath}#ngo`,
      name,
      image,
      description:
        entry.description ||
        `${name}, a South Asian non-profit organization serving the Dallas–Fort Worth community.`,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
      ...(address
        ? {
            address: {
              '@type': 'PostalAddress',
              streetAddress: address,
              addressLocality: 'Dallas',
              addressRegion: 'TX',
              addressCountry: 'US',
            },
          }
        : {}),
      areaServed: 'Dallas–Fort Worth metroplex',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Non-Profit Organizations', item: `${BASE_URL}/non-profit-org` },
        { '@type': 'ListItem', position: 3, name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other listings in this category.
  const related = mc.items
    .map((e: any, i: number) => ({ item: e, key: nonProfitSlug(e, i) }))
    .filter((r: any) => r.key !== slug)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${image}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/non-profit-org"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Non-Profit Organizations
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
              {address ? `${address} · Dallas–Fort Worth` : 'Dallas–Fort Worth'}
            </span>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.description ? `${entry.description}. ` : ''}
            {name} is a non-profit organization serving the South Asian community in the
            Dallas–Fort Worth metroplex. From community programs and volunteer opportunities to
            cultural and charitable initiatives, {name} works to give back to desi families
            across DFW. Reach out directly to learn about programs, how to volunteer, and ways
            to support their mission.
          </p>

          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={28} />
            </div>
          )}
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {contacts.map((c) => (
              <a
                key={c.url}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <c.Icon className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{c.label}</span>
              </a>
            ))}
            {address && (
              <a
                href={maps}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{address}</span>
              </a>
            )}
            {contacts.length === 0 && !address && (
              <p className="text-gray-400 text-[13px]">Contact information coming soon.</p>
            )}
          </div>
          {contacts.length > 0 && (
            <a
              href={contacts[0].url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Get Involved
            </a>
          )}
          {address && (
            <a
              href={maps}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:bg-[#45a943] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <MapPin className="w-4 h-4" />
              View on Google Maps
            </a>
          )}
        </div>
      </div>

      {/* Internal links: other listings */}
      {related.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">
            More Non-Profit Organizations in DFW
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {related.map(({ item, key }: any) => (
              <Link
                key={key}
                to={`/non-profit-org/${key}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <Globe className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/non-profit-org', label: 'All Non-Profit Organizations in DFW' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/temples', label: 'Temples in DFW' },
            { to: '/senior-citizen', label: 'Senior Citizen Services' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="bg-white border border-gray-100 rounded-full px-4 py-2 text-[12px] font-medium text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
